import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import {
  deductionRecordsApi,
  type DeductionItemResult,
  type DeductionRecordItem,
} from '../../api/deduction.api';
import type { ColumnType } from 'antd/es/table';
import { EllipsisText } from '../../components/EllipsisCell';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { DetailDrawer } from '../../components/DetailDrawer';
import { startAction } from '../../observability/otel';
import { useDraggableModal } from '../../components/useDraggableModal';
import { ApiError } from '../../api/transport';
import { useHasPermission } from '../../auth/HasPermission';

const { Title, Text } = Typography;

/** 结果中文标签（含冲正与期限卡覆盖，6-1 批次抽屉同口径）。 */
export const deductionResultLabel: Record<DeductionItemResult, { text: string; color: string }> = {
  DEDUCTED: { text: '成功扣减', color: 'green' },
  SKIPPED_NO_BALANCE: { text: '余额不足', color: 'orange' },
  SKIPPED_ACCOUNT_INVALID: { text: '账户失效', color: 'red' },
  REVERSED: { text: '已冲正', color: 'default' },
  // O-14：期限卡覆盖（不进本页任一区块，仅标签兜底防漂移）
  PERIOD_COVERED: { text: '期限卡覆盖', color: 'blue' },
};

/** 课时类别中文标签（单账户双池：本笔扣减所属池）。 */
const lessonKindLabel: Record<string, string> = {
  FORMAL: '正式课',
  GIFTED: '赠送课',
};

/** 历史区块筛选值（失败与已冲正合集，SC-01/SC-11）。 */
const HISTORY_RESULTS = 'SKIPPED_NO_BALANCE,SKIPPED_ACCOUNT_INVALID,REVERSED';

function formatDateTime(value: string): string {
  return dayjs(value).format('YYYY-MM-DD HH:mm');
}

/** ADM-23 消课异常与冲正：失败与冲正历史 + 发起冲正双区块（deduction-reversal）。 */
export function DeductionExceptionPage() {
  // 课次直达筛选（批次抽屉「去处理」携带 ?sessionId=，双区块收敛至该课次明细）
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSessionId = searchParams.get('sessionId') ?? undefined;
  const [sessionFilter, setSessionFilter] = useState<string | undefined>(
    initialSessionId,
  );
  // 失败与冲正历史区块
  const [historyRows, setHistoryRows] = useState<DeductionRecordItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  // 发起冲正区块
  const [deductedRows, setDeductedRows] = useState<DeductionRecordItem[]>([]);
  const [deductedTotal, setDeductedTotal] = useState(0);
  const [deductedPage, setDeductedPage] = useState(1);
  const [studentKeyword, setStudentKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  // 异常详情抽屉
  const [viewingId, setViewingId] = useState<string | null>(null);
  // 冲正入口需 ADM-23 REVERSE 专有动词；无权限隐藏操作列，越权由后端 403 兜底
  const canReverse = useHasPermission('ADM-23', 'REVERSE');

  // 冲正确认对话框（幂等键绑定对话框会话：打开时生成，重试复用，SC-07/SC-08）
  const draggableModal = useDraggableModal();
  const pendingReasonRef = useRef('');
  const idempotencyKeyRef = useRef<string>('');

  const pageSize = 20;

  const loadHistory = useCallback(
    async (page: number, sessionId?: string) => {
      setLoading(true);
      try {
        const res = await deductionRecordsApi.listDeductions({
          result: HISTORY_RESULTS,
          sessionId,
          page,
          pageSize,
        });
        setHistoryRows(res.items);
        setHistoryTotal(res.total);
        setHistoryPage(page);
      } catch {
        message.error('加载失败与冲正历史失败');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const loadDeducted = useCallback(
    async (page: number, keyword: string, sessionId?: string) => {
      setLoading(true);
      try {
        const res = await deductionRecordsApi.listDeductions({
          result: 'DEDUCTED',
          studentName: keyword || undefined,
          sessionId,
          page,
          pageSize,
        });
        setDeductedRows(res.items);
        setDeductedTotal(res.total);
        setDeductedPage(page);
      } catch {
        message.error('加载可冲正列表失败');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void loadHistory(1, sessionFilter);
    void loadDeducted(1, '', sessionFilter);
  }, [loadHistory, loadDeducted, sessionFilter]);

  /** 清除课次直达筛选：移除 URL 参数并回到全量双区块。 */
  const clearSessionFilter = useCallback(() => {
    setSearchParams({}, { replace: true });
    setSessionFilter(undefined);
  }, [setSearchParams]);

  const reloadBoth = useCallback(() => {
    void loadHistory(historyPage, sessionFilter);
    void loadDeducted(deductedPage, studentKeyword, sessionFilter);
  }, [loadHistory, loadDeducted, historyPage, deductedPage, studentKeyword, sessionFilter]);

  const historyResizable = useResizableColumns<DeductionRecordItem>([
    { title: '课次日期', dataIndex: 'sessionDate' },
    { title: '班级', dataIndex: 'className', render: (v) => <EllipsisText value={v} /> },
    { title: '学生', dataIndex: 'studentName' },
    {
      title: '课时类别',
      dataIndex: 'lessonKind',
      render: (v) => (v ? lessonKindLabel[v] ?? v : '-'),
    },
    { title: '异常原因', dataIndex: 'failReason', render: (v) => v ?? '-' },
    {
      title: '处理状态',
      dataIndex: 'result',
      render: (v: DeductionItemResult) => {
        const label = deductionResultLabel[v];
        return <Tag color={label.color}>{label.text}</Tag>;
      },
    },
    { title: '操作人', dataIndex: 'operatorName', render: (v) => v ?? '-' },
    { title: '时间', dataIndex: 'createdAt', render: (v) => formatDateTime(v) },
    {
      title: '操作',
      render: (_, record) => (
        <a
          onClick={() => setViewingId(record.id)}
        >
          查看
        </a>
      ),
    },
  ]);

  // 冲正操作列（显式列类型保住条件展开后的推断；无 REVERSE 权限不渲染该列）
  const reverseActionColumn: ColumnType<DeductionRecordItem> = {
    title: '操作',
    render: (_, record) => (
      <a
        onClick={() => {
          pendingReasonRef.current = '';
          idempotencyKeyRef.current = crypto.randomUUID();
          openReverseConfirm(record);
        }}
      >
        冲正
      </a>
    ),
  };

  const reversalResizable = useResizableColumns<DeductionRecordItem>([
    { title: '课次', dataIndex: 'sessionDate' },
    { title: '班级', dataIndex: 'className', render: (v) => <EllipsisText value={v} /> },
    { title: '学生', dataIndex: 'studentName' },
    { title: '扣减数量', dataIndex: 'quantity' },
    { title: '当前余额', dataIndex: 'currentAvailable', render: (v) => v ?? '-' },
    { title: '扣减时间', dataIndex: 'createdAt', render: (v) => formatDateTime(v) },
    ...(canReverse ? [reverseActionColumn] : []),
  ]);

  const historyColumns = historyResizable.columns;
  const historyComponents = historyResizable.components;
  const reversalColumns = reversalResizable.columns;
  const reversalComponents = reversalResizable.components;

  const viewing = historyRows.find((row) => row.id === viewingId) ?? null;

  function closeReverse() {
    pendingReasonRef.current = '';
    idempotencyKeyRef.current = '';
  }

  /** 打开冲正确认对话框（命令式 useDraggableModal，含必填原因与 P0 动作遥测，SC-07/SC-08/SC-10）。 */
  function openReverseConfirm(record: DeductionRecordItem) {
    draggableModal.confirm({
      title: '冲正确认',
      okText: '确认冲正',
      cancelText: '取消',
      okButtonProps: { danger: true },
      content: (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Alert
            type="warning"
            showIcon
            message={`冲正后该学生课时余额将恢复 ${record.quantity}，原流水不会被删除`}
          />
          <Text>
            学生：{record.studentName}（{record.studentNo}）
          </Text>
          <Text>原扣减数量：{record.quantity}</Text>
          <Text>
            当前余额：
            {record.currentAvailable === null ? '-' : record.currentAvailable}
          </Text>
          <Input.TextArea
            placeholder="请填写冲正原因，将写入审计记录"
            defaultValue=""
            onChange={(event) => {
              pendingReasonRef.current = event.target.value;
            }}
            rows={3}
            maxLength={255}
          />
        </Space>
      ),
      onOk: async () => {
        const reason = pendingReasonRef.current.trim();
        if (!reason) {
          message.error('请填写冲正原因');
          // 拒绝关闭：返回 rejected promise 使对话框保持打开
          return Promise.reject(new Error('reason-required'));
        }
        const action = startAction('deduction.reverse');
        try {
          const result = await deductionRecordsApi.reverseDeduction(
            record.id,
            reason,
            idempotencyKeyRef.current,
          );
          action.end();
          message.success(
            `已完成冲正：恢复 ${result.restoredQuantity} 课时，当前余额 ${result.availableAfter}`,
          );
          closeReverse();
          reloadBoth();
        } catch (error) {
          action.end();
          if (error instanceof ApiError) {
            // 409 业务错误（已被冲正/无流水可冲正/账户作废）展示服务端中文提示并刷新列表
            message.warning(error.message);
            closeReverse();
            reloadBoth();
            return;
          }
          message.error('冲正失败，请稍后重试');
          return Promise.reject(error as Error);
        }
      },
    });
  }

  return (
    <div>
      <div className="page-header-row">
        <div>
          <Title level={4} style={{ margin: 0 }}>
            消课异常与冲正
          </Title>
          <Text type="secondary">
            更正以新增冲正流水撤销原扣减，原流水不会被删除，全程留痕可审计
          </Text>
        </div>
      </div>

      {sessionFilter && (
        <Alert
          type="info"
          showIcon
          closable
          style={{ marginTop: 12 }}
          message="已按来源课次筛选记录（批次详情「去处理」直达），下方双区块仅展示该课次明细"
          action={
            <Button size="small" onClick={clearSessionFilter}>
              查看全部
            </Button>
          }
        />
      )}

      <Card title="失败与冲正历史" size="small" style={{ marginTop: 12 }}>
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={historyColumns}
          components={historyComponents}
          dataSource={historyRows}
          pagination={{
            current: historyPage,
            pageSize,
            total: historyTotal,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            onChange: (page) => void loadHistory(page, sessionFilter),
          }}
        />
      </Card>

      <Card title="发起冲正" size="small" style={{ marginTop: 12 }}>
        <Space style={{ marginBottom: 12 }}>
          <Input.Search
            placeholder="按学生姓名筛选"
            allowClear
            style={{ width: 240 }}
            onSearch={(value) => {
              setStudentKeyword(value);
              void loadDeducted(1, value, sessionFilter);
            }}
          />
        </Space>
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={reversalColumns}
          components={reversalComponents}
          dataSource={deductedRows}
          locale={{
            emptyText:
              studentKeyword.trim() !== ''
                ? '无成功扣减记录'
                : '暂无成功扣减记录',
          }}
          pagination={{
            current: deductedPage,
            pageSize,
            total: deductedTotal,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            onChange: (page) => void loadDeducted(page, studentKeyword, sessionFilter),
          }}
        />
      </Card>

      {viewing && (
        <DetailDrawer
          title="异常详情"
          open
          onClose={() => setViewingId(null)}
          width={520}
        >
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="明细编号">{viewing.id}</Descriptions.Item>
            <Descriptions.Item label="学生">
              {viewing.studentName}（{viewing.studentNo}）
            </Descriptions.Item>
            <Descriptions.Item label="课次日期">{viewing.sessionDate}</Descriptions.Item>
            <Descriptions.Item label="班级">{viewing.className}</Descriptions.Item>
            <Descriptions.Item label="原扣减数量">{viewing.quantity}</Descriptions.Item>
            <Descriptions.Item label="当前余额">
              {viewing.currentAvailable === null ? '-' : viewing.currentAvailable}
            </Descriptions.Item>
            <Descriptions.Item label="异常原因">{viewing.failReason ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="处理状态">
              <Tag color={deductionResultLabel[viewing.result].color}>
                {deductionResultLabel[viewing.result].text}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="操作人">{viewing.operatorName ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="时间">{formatDateTime(viewing.createdAt)}</Descriptions.Item>
            {viewing.result === 'REVERSED' && (
              <>
                <Descriptions.Item label="冲正业务原因">
                  {viewing.reversalReason ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label="冲正操作人">
                  {viewing.reversalOperatorName ?? '-'}
                </Descriptions.Item>
              </>
            )}
          </Descriptions>
          <div style={{ marginTop: 12 }}>
            {viewing.result === 'REVERSED' ? (
              <Text type="secondary">该记录已冲正，课时余额已恢复。</Text>
            ) : (
              <Text type="secondary">
                该记录当日已跳过消课，需家长续费或核实账户后由老师重新发起消课，无需冲正
              </Text>
            )}
          </div>
        </DetailDrawer>
      )}

    </div>
  );
}
